import React, { useState } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface AdminDiscountFormProps {
  eventPrice: number;
  onDiscountApplied: (discountData: {
    discount_type: "percentage" | "fixed_amount";
    discount_value: number;
    reason: string;
  }) => void;
  onDiscountRemoved: () => void;
  isDiscountApplied: boolean;
  appliedDiscountInfo?: {
    type: "percentage" | "fixed_amount";
    value: number;
  } | null;
}

export default function AdminDiscountForm({
  eventPrice,
  onDiscountApplied,
  onDiscountRemoved,
  isDiscountApplied,
  appliedDiscountInfo,
}: AdminDiscountFormProps) {
  const [discountType, setDiscountType] = useState<
    "percentage" | "fixed_amount"
  >("percentage");
  const [discountValue, setDiscountValue] = useState<string>("10");
  const [discountReason, setDiscountReason] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const { toast } = useToast();

  const handleApplyDiscount = () => {
    if (!discountReason.trim()) {
      setError("Discount reason is required.");
      return;
    }
    const parsedVal = parseFloat(discountValue) || 0;
    if (parsedVal <= 0) {
      setError("Discount amount or percentage must be greater than 0.");
      return;
    }

    onDiscountApplied({
      discount_type: discountType,
      discount_value: parsedVal,
      reason: discountReason,
    });
    setError(null);

    const displayText =
      discountType === "percentage"
        ? `${discountValue}% off per seat`
        : `$${parsedVal.toFixed(2)} off per seat`;

    toast({
      title: "Admin Discount Applied",
      description: `${displayText}. Reason: ${discountReason}`,
    });
  };

  const handleRemoveDiscount = () => {
    onDiscountRemoved();
  };

  if (isDiscountApplied && appliedDiscountInfo) {
    const displayText =
      appliedDiscountInfo.type === "percentage"
        ? `${appliedDiscountInfo.value}% off per seat`
        : `$${appliedDiscountInfo.value.toFixed(2)} off per seat`;

    return (
      <div className="flex items-center gap-2 text-sm py-2">
        <Check className="h-4 w-4 text-purple-500 flex-shrink-0" />
        <span className="text-purple-700 dark:text-purple-300 font-medium">
          Admin discount &mdash; {displayText}
        </span>
        <button
          className="ml-auto text-sm text-muted-foreground underline hover:no-underline"
          onClick={handleRemoveDiscount}
          type="button"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium py-1"
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
      >
        {isExpanded ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
        Apply admin discount
      </button>

      {isExpanded && (
        <div className="mt-3 pl-5 space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <label className="flex items-center gap-1.5 dark:text-gray-200">
              <input
                checked={discountType === "percentage"}
                name="discount-type"
                onChange={() => setDiscountType("percentage")}
                type="radio"
                value="percentage"
              />
              Percentage
            </label>
            <label className="flex items-center gap-1.5 dark:text-gray-200">
              <input
                checked={discountType === "fixed_amount"}
                name="discount-type"
                onChange={() => setDiscountType("fixed_amount")}
                type="radio"
                value="fixed_amount"
              />
              Fixed Amount
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="discount-value">
                {discountType === "percentage" ? "Percentage" : "Amount (per seat)"}
              </Label>
              <Input
                className="h-9"
                id="discount-value"
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === "percentage" ? "10" : "25.00"}
                type="number"
                value={discountValue}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="discount-reason">
                Reason
              </Label>
              <Input
                className="h-9"
                id="discount-reason"
                onChange={(e) => setDiscountReason(e.target.value)}
                placeholder="Required"
                type="text"
                value={discountReason}
              />
            </div>
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}

          <Button
            onClick={handleApplyDiscount}
            size="sm"
            variant="outline"
          >
            Apply Admin Discount
          </Button>
        </div>
      )}
    </div>
  );
}
